import React, {useRef, useEffect, useState, useContext, lazy, Suspense} from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import 'mapbox-gl/dist/mapbox-gl.css'; // Make sure to import Mapbox GL styles
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'; // Import MapboxGeocoder styles
import config from "../config";
import {DataContext} from "../contexts/DataContext";
import {Divider, Button, Paper, Typography} from "@mui/material"; // Added Button, Paper, Typography
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import LoadingBackdrop, {LoadingBar} from './loadingBackdrop';
import OfflineNotice from './offlineNotice';
import SelectedStorm from "./selectedStorm";
import PopUpReport from './reportPopUp';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TceTitle } from './img/tce-title.svg';
import { ReactComponent as FleTitle } from './img/combined_fle.svg';
import { ReactComponent as ArcLogo } from './img/arc-logo.svg';
import Settings from "./settings";
import SelectedExploration from "./selectedExploration";
import {formatDate, TODAY_DATE, PREV_DTG, NEXT_DTG} from '../utils/dateFunctions'
import {coordinatesGeocoder, fly, flyToFlood} from '../utils/mapFunctions';
import {getCountryDataJsonUrl, getCountryStochasticDataJsonUrl} from '../utils/data';
import {fetchJSON} from '../utils/fetchFunctions';
import {
    drawStorm,
    addSourceStorm,
    updateSourceStorm,
    showHideStorm, showHideExcessRain, addCustomGeoJSONLayer, addSourceLayerFlood, removeSourceLayerFlood,
    loadDeferredLayerData
} from "../utils/mapLayoutFunctions";
import {windCategory, hexToRgb, markOnce} from "../utils/miscFunctions";
import {
    URL_ROOT,
    URL_ROOT_FLE,
    WIND_COLORS,
    ICONS,
    MIN_ZOOM,
    MAX_ZOOM,
    POPULATION_GRADIENT_COLORS,
    VALUE_GRADIENT_COLORS,
    EXCESS_RAIN_GRADIENT_COLORS,
    POP_EXPOSED_BAND_1_GRADIENT_COLORS,
    COUNTRIES,
    WIDTH_FLOOD_OVERLAY,
    HEIGHT_FLOOD_OVERLAY, FLOOD_DEPTH_GRADIENT_COLORS, ECONOMIC_URLS, POPULATION_URLS, COUNTRIES_STOCHASTIC,
    COUNTRIES_LOAD_ORDER, COUNTRIES_LOAD_CONCURRENCY
} from '../constants/constants';
import Languages from "./languages";
import {getUrlGeoJSON, getUrlGeoJSONs} from "../utils/nasaWeatherFunctions";
import SelectedFlood from "./selectedFlood";
import Legend from "./legend";
import {findDepthByBand} from "../utils/depth";
import ReportFloodPopUp from "./reportFloodPopUp";
import OngoingEventsOverlay from "./ongoingEventsOverlay";

// Loaded when a flood is first selected (brings the chart library)
const FloodOverlay = lazy(() => import("./floodOverlay"));
// Loaded when a storm is first selected (brings the table of the storm and the risk parameters store)
const StormOverlay = lazy(() => import("./stormOverlay"));



export default function MapContainer() {

    const mapContainer = useRef(null);
    const map = useRef(null);
    const [lng, setLng] = useState(32.5);
    const [lat, setLat] = useState(-23);
    const [zoom, setZoom] = useState(4.3);
    const [pitch, setPitch] = useState(0);
    const [stormName, setStormName] = useState('');
    const [stormId, setStormId] = useState('');
    const [stormsData, setStormsData] = useState({});
    const [selectedStormData, setSelectedStormData] = useState([]);
    const [selectedFloodData, setSelectedFloodData] = useState();
    const [todayData, setTodayData] = useState([]);
    const [currentLosses, setCurrentLosses] = useState([]);
    const [vmax, setVmax] = useState(null);
    const [maxSurge, setMaxSurge] = useState(null);
    const [stormPositions, setStormPositions] = useState([]);
    const [openAlert, setOpenAlert] = useState(true);
    // data per country code: filled country by country as the files arrive (flood data),
    // and for the country of a selected flood only (stochastic data)
    const [countryData, setCountryData] = useState({});
    const [countryStochasticPopulationData, setCountryStochasticPopulationData] = useState({});
    const [countryStochasticEconomicData, setCountryStochasticEconomicData] = useState({});
    const [stochasticLoading, setStochasticLoading] = useState({}); // {country: true} while its stochastic data is downloading
    const stochasticRequested = useRef({});
    const [mapLoaded, setMapLoaded] = useState(false);
    const [floodLoadError, setFloodLoadError] = useState(false);

    // Initial loading progress (drives the loader): the map + the flood data file of every country
    // pending: steps not done yet, step: last step done, failed: steps that could not be loaded,
    // errors: failures not yet dismissed by the user (all translated in loader.steps)
    const LOAD_STEPS = [
        {key: 'map'},
        ...COUNTRIES.map(country => ({key: 'country', country})),
    ];
    const [loadProgress, setLoadProgress] = useState({done: 0, step: null, pending: LOAD_STEPS, failed: [], errors: []});
    const loadStepFailed = (key, country) => setLoadProgress(p => ({...p, failed: [...p.failed, {key, country}], errors: [...p.errors, {key, country}]}));
    const loadStepDone = (key, country) => setLoadProgress(p => ({
        ...p,
        done: p.done + 1,
        step: {key, country},
        pending: p.pending.filter(s => !(s.key === key && s.country === country)),
    }));
    // countries whose flood data is still awaited / could not be loaded: greyed out in the menu of the floods
    const loadingCountries = loadProgress.pending.filter(s => s.key === 'country').map(s => s.country);
    const failedCountries = loadProgress.failed.filter(s => s.key === 'country').map(s => s.country);

    // State to hold the parsed list of ongoing events
    const [ongoingEvents, setOngoingEvents] = useState([]);

    const { t, ready } = useTranslation();

    // Loader step label (t() returns the raw key until the translations are loaded)
    const loadStepLabel = (step) => t('loader.steps.' + step.key, {country: step.country ? t('constants.countries.' + step.country) : ''});
    // Names what is still loading (the first two steps when there are more), then the last step done
    const loadStepText = !ready ? '' :
        loadProgress.pending.length > 0
            ? t('loader.pending', {steps: loadProgress.pending.slice(0, 2).map(loadStepLabel).join(', ') + (loadProgress.pending.length > 2 ? '…' : '')})
            : loadProgress.step ? loadStepLabel(loadProgress.step) : '';

    //TODO: check what properties can be removed from DataContext
    // /!\ BEWARE of the usage in all the file, e.g. current_storm property (which might be redundant btw, is used to go back to the current storm when displaying the current storm
    // - LOOK FOR redundant and/or obsolete properties
    const {
        storm,
        bounds,
        currentStorm,
        current_storm,
        selectedStorm,
        updateSelectedStorm,
        selectedFlood,
        updateSelectedFlood,
        selectedCountry,
        updateSelectedCountry,
        displayedFlood,
        updateDisplayedFlood,
        adm0,
        adm1,
        adm2,
        population,
        value,
        stochastic_heatmap,
        stochastic_trajectories,
        pastPeakWind,
        pastPeakWater,
        pastRainTotal,
        accRain3h,
        accRain1d,
        fcstRain1d,
        fcstRain5d,
        accRain1dVideo,
        fcstRain1dVideo,
        storm_position_trajectory,
        storm_position_points,
        storm_position_labels,
        updateStormPositionPoints,
        updateStormPositionLabels,
        updateCurrentStorm,
        openStormOverlay,
        updateOpenStormOverlay,
        selectedDTG,
        updateSelectedDTG,
        stormsToShow,
        updateLayerStatus
    } = useContext(DataContext);

    // shows the data of a layer switched on: downloads it if it was deferred, and reports the download to the menus
    const loadLayerData = (layerId) => loadDeferredLayerData(map.current, layerId, status => updateLayerStatus(layerId, status))


    //// function to clean the current map from the selectedStorm
    const clearMapSelectedStorm = () => {
        showHideStorm(map.current, selectedStorm, layers, false)
    }

    //// functions  to fetch data

    const getCountryData = (country) => {
        const url = getCountryDataJsonUrl(country);
        return fetchJSON(url)
            .then(data => {
                if (data && data.ongoing !== undefined) {
                    const loaded = {
                        [country]: {
                            ongoing: data.ongoing,
                            ongoing_event_year: data.ongoing_event_year,
                            ongoing_event_month: data.ongoing_event_month,
                            ongoing_event_day: data.ongoing_event_day,
                            ongoing_event: data.ongoing_event,
                            stats: data.stats,
                            total_events_country: data.total_events_country,
                            total_days_country: data.total_days_country,
                            peak_year: data.peak_year,
                            year_by_year: data.year_by_year,
                        }
                    };
                    setCountryData(previous => ({...previous, ...loaded})); // available at once, without waiting for the other countries
                    return loaded;
                } else {
                    return null; // Return null when data is not found
                }
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                if (error.connection) loadStepFailed('country', country); // a connection failure is reported by the loader
                return null; // Return null on error
            })
            .finally(() => loadStepDone('country', country));
    }

    const getCountryStochasticEconomicData = (country) => {
        const url = getCountryStochasticDataJsonUrl(country, 'economic');

        return fetchJSON(url)
            .then(data => {
                return {
                    [country]: data
                }
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                if (error.connection) setFloodLoadError(true); // a connection failure is shown in an alert
                return null; // Return null on error
            });
    }

    const getCountryStochasticPopulationData = (country) => {
        const url = getCountryStochasticDataJsonUrl(country, 'population');

        return fetchJSON(url)
            .then(data => {
                return {
                    [country]: data
                }
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                if (error.connection) setFloodLoadError(true); // a connection failure is shown in an alert
                return null; // Return null on error
            });
    }



    // get all the storms that hit
    // get flood data
    const getFloodData = (url) => {
        setFloodLoadError(false)
        fetchJSON(url)
            .then(data => {
                setSelectedFloodData(data)
            })
            .catch(err => {
                if (err.connection) setFloodLoadError(true) // a connection failure is shown in an alert
                throw err
            })
    }

    // get storm data
    //TODO: REMOVE all the unnecessary variables (see below function: it's the contraction of all of them)
    const getStormData = (url) => {
        setStormsData({})
        setSelectedStormData([])
        fetchJSON(url)
            .then(data => {
                const setStorms = new Set(data.features.map(x => x.properties.ATCFID))
                const objStorms = {}
                setStorms.forEach(stormId => {
                    const stormDataFilter = data.features.filter(x => x.properties.ATCFID === stormId)
                    const todayDataFilter = stormDataFilter.filter(x => x.properties.DTG <= formatDate(TODAY_DATE)).slice(-1)[0].properties
                    const stormPositionsFilter = stormDataFilter.map(x => ({DTG: x.properties.DTG, coordinates: x.geometry.coordinates, VMAX: x.properties.VMAX}))
                    const vmaxFilter = Math.max(...stormDataFilter.map(x => x.properties.VMAX))
                    let key = stormId
                    objStorms[key] = {data: stormDataFilter, todayData: todayDataFilter, stormPositions: stormPositionsFilter, vmax: vmaxFilter}
                })
                setStormsData(objStorms)
                setSelectedStormData(objStorms[selectedStorm.id])
            })
    }

    // get storm id and name
    const getLatestStormID = (url) => {
        setStormId('')
        setStormName('')
        fetchJSON(url)
            .then(data => {
                setStormId(data.features[0].properties.ATCFID)
                setStormName(data.features[0].properties.NAME)
            })
    }

    // get current data
    const getCurrentData = (url) => {
        setTodayData([])
        fetchJSON(url)
            .then((data) => {
                setTodayData(data.features.filter(x => x.properties.DTG <= formatDate(TODAY_DATE) && x.properties.BASIN ? x.properties.BASIN === 'SH' : true).slice(-1)[0].properties)
            })
    }

    const getStormPositions = (url) => {
        fetchJSON(url)
            .then((resp) => resp.features.filter(x => x.geometry.type === 'Point' && x.properties.BASIN === 'SH' && x.properties.LON <= 113))
            .then((resp) => {
                setStormPositions(resp.map(x => ({DTG: x.properties.DTG, coordinates: x.geometry.coordinates, VMAX: x.properties.VMAX})))
            })
    }

    // get vmax
    const getVmax = (url) => {
        fetchJSON(url)
            .then((data) => {
                if (selectedStorm.type === 'current') {
                    setVmax(Math.max(...data.features.filter(x => x.properties.BASIN === 'SH').map(x => x.properties.VMAX)))
                } else {
                    setVmax(Math.max(...data.features.map(x => x.properties.VMAX)))
                }
            })
    }

    // get max surgegit
    const getMaxSurge = (url) => {
        fetchJSON(url)
            .then((data) => {
                // TODO: apply conditional treatment if selectedStorm.type === 'current', filtering out basin 'SH' (cf. getVmax(url) )
                const surge = Math.max(...data.features.filter(x => x.properties.field === 'past_peak_water').map(x => x.properties.val1))
                setMaxSurge(Math.round((surge + Number.EPSILON) * 100) / 100)
            })
    }

    // get current losses
    const getCurrentLosses = (url) => {
        setCurrentLosses([])
        fetchJSON(url)
            .then(data => data.records.map((x => ({adm0_name: x.adm0_name, loss: x.loss, population: x.population, wind_cat: x.wind_cat, adm1: x.adm1}))))
            .then((data) => {
                setCurrentLosses(data)
            })
    }


    //// layers => layerId: isVisible
    const layers = {
        'pastRainTotal': pastRainTotal,
        'band_1': pastPeakWind,
        'band_5': pastPeakWater,
        'precip_3hr': accRain3h,
        'losses_band_1': accRain1d, //
        'losses_band_5': fcstRain1d,
        'losses_band_11': fcstRain5d,
        'band_11': accRain1dVideo,
        'storm trajectory': storm_position_trajectory,
        'storm points': storm_position_points,
        'storm info': storm_position_labels,
    }

    const layersPast = {
        'storm-past-wind-layer': true,
        'storm-fcst-wind-layer': false,
        'storm-fcst-trajectory': false,
    }

    /// *** This might have to be removed
    const layersAdm = {
        'adm0': adm0,
        'adm1': adm1,
        'adm2': adm2
    }

    function pulsatingDot(size, color) {
        const pulsingDot = {
            width: size,
            height: size,
            data: new Uint8Array(size * size * 4),

            // When the layer is added to the map,
            // get the rendering context for the map canvas.
            onAdd: function () {
                const canvas = document.createElement('canvas');
                canvas.width = this.width;
                canvas.height = this.height;
                this.context = canvas.getContext('2d');
            },

            // Call once before every frame where the icon will be used.
            render: function () {
                const duration = 1000;
                const t = (performance.now() % duration) / duration;

                const radius = (size / 2) * 0.3;
                const outerRadius = (size / 2) * 0.7 * t + radius;
                //const outerRadius = 2 * radius + radius * Math.sin((size / 2) * 0.7 * t / (2 * 2 * 3.14));
                const context = this.context;

                // Draw the outer circle.
                context.clearRect(0, 0, this.width, this.height);
                context.beginPath();
                context.arc(
                    this.width / 2,
                    this.height / 2,
                    outerRadius,
                    0,
                    Math.PI * 2
                );
                if (color[0] === '#') {
                    let rgb = hexToRgb(color)
                    context.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`;
                } else {
                    context.fillStyle = `rgba(255, 200, 200, ${1 - t})`;
                }
                context.fill();

                // Draw the inner circle.
                context.beginPath();
                context.arc(
                    this.width / 2,
                    this.height / 2,
                    radius,
                    0,
                    Math.PI * 2
                );
                context.fillStyle = color;
                context.strokeStyle = 'white';
                context.lineWidth = 2 + 4 * (1 - t);
                context.fill();
                context.stroke();

                // Update this image's data with data from the canvas.
                this.data = context.getImageData(
                    0,
                    0,
                    this.width,
                    this.height
                ).data;

                // Continuously repaint the map, resulting
                // in the smooth animation of the dot.
                map.current.triggerRepaint();

                // Return `true` to let the map know that the image was updated.
                //return true;

                // clean up on unmount
                return () => map.current.remove();

            }
        };
        return pulsingDot
    }

    //TODO: is the next line necessary?
    let selectedPulsatingDot = pulsatingDot(100, 'blue')


    //// initiating empty geojson for pulsing dot
    let static_dot_geojson = {
        "type": "FeatureCollection",
        "features": []
    };

    // add marker with coordinates for pulsating dot
    let marker = {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: []
        }
    };


    ////// Side effects

    //// 1x initialization of the map
    useEffect(() => {
        if (map.current) {
            console.log('map already initialized')
            return; // initialize map only once
        }
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            //style: 'mapbox://styles/bertranddelvaux/cl14sbxgz008q14pcjgdtm1ww', // Minimo min version
            //style: 'mapbox://styles/bertranddelvaux/cleiohuke001h01nz3myclnup?optimize=true', // Minimo min version
            style: 'mapbox://styles/mapbox/light-v11',
            //style: 'mapbox://styles/mapbox/standard-beta',
            antialias: true,
            //style: 'mapbox://styles/mapbox/dark-v11',
            center: [ lng, lat ],
            zoom: zoom,
            pitch: pitch,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
            renderWorldCopies: false,
            attributionControl: false, // Check, but legally, it should be set to 'true'
            accessToken: config.mapboxToken,
        });
        console.log('Init useEffect')

        // Add the control to the map.
        map.current.addControl(
            new MapboxGeocoder({
                accessToken: config.mapboxToken,
                localGeocoder: coordinatesGeocoder,
                zoom: 4,
                placeholder: 'Enter a place, Lat, Lon',
                mapboxgl: mapboxgl,
                reverseGeocode: true
            }),
            'bottom-left'
        );


        // Add zoom and rotation controls to the map.
        map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left');

        // Add zoom and rotation controls to the map.
        map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-left');

        /* //TODO: this can be removed
                // Adding cyclone icons on the map
                ICONS.forEach(icon => {
                    map.current.loadImage(icon.url, function(error, image) {
                        map.current.addImage(icon.id, image)
                    })
                })*/

    }, [])

    ///// 1x static layers
    useEffect(() => {
        //TODO: name each effect more appropriately in the console
        console.log('Other useEffect')
        if (!map.current) {
            console.log('map not yet initialized')
            return;
        }

        // the style is enough to add the layers: the 'load' event would also wait for all the tiles of the map
        map.current.once('style.load', () => {

            console.log('map on style.load')
            setMapLoaded(true)
            loadStepDone('map')

            /* // Insert the layer beneath any symbol layer.
                        const layers = map.current.getStyle().layers;
                        const labelLayerId = layers.find(
                            (layer) => layer.type === 'symbol' && layer.layout['text-field']
                        ).id;

            // The 'building' layer in the Mapbox Streets
            // vector tileset contains building height data
            // from OpenStreetMap.
                        map.current.addLayer(
                            {
                                'id': 'add-3d-buildings',
                                'source': 'composite',
                                'source-layer': 'building',
                                'filter': ['==', 'extrude', 'true'],
                                'type': 'fill-extrusion',
                                'minzoom': 15,
                                'paint': {
                                    'fill-extrusion-color': '#aaa',

            // Use an 'interpolate' expression to
            // add a smooth transition effect to
            // the buildings as the user zooms in.
                                    'fill-extrusion-height': [
                                        'interpolate',
                                        ['linear'],
                                        ['zoom'],
                                        15,
                                        0,
                                        15.05,
                                        ['get', 'height']
                                    ],
                                    'fill-extrusion-base': [
                                        'interpolate',
                                        ['linear'],
                                        ['zoom'],
                                        15,
                                        0,
                                        15.05,
                                        ['get', 'min_height']
                                    ],
                                    'fill-extrusion-opacity': 0.6
                                }
                            },
                            labelLayerId
                        );*/


            const url_losses_json = URL_ROOT + 'data/moz/impacts/2023/02/10/for_moz_ts_rd20230210T0000Z_population_impacts.geojson'
            addCustomGeoJSONLayer(map.current, 'losses_band_1', url_losses_json, 'band_1', POP_EXPOSED_BAND_1_GRADIENT_COLORS, accRain1d)

            addCustomGeoJSONLayer(map.current, 'losses_band_5', url_losses_json, 'band_5', POP_EXPOSED_BAND_1_GRADIENT_COLORS, fcstRain1d)

            addCustomGeoJSONLayer(map.current, 'losses_band_11', url_losses_json, 'band_11', POP_EXPOSED_BAND_1_GRADIENT_COLORS, fcstRain5d)

            //////////////////////////////////////////




            //// Add Custom Layer 1d 3h
            let datasetPPM = 'precip_3hr'
            let field = 'precip'
            let startDate = formatDate(TODAY_DATE, '-')
            let endDate = formatDate(TODAY_DATE, '-')
            const urlGeoJSONs3h = getUrlGeoJSONs(datasetPPM, startDate, endDate)
            addCustomGeoJSONLayer(map.current, datasetPPM, urlGeoJSONs3h[0], field, EXCESS_RAIN_GRADIENT_COLORS, accRain3h)

            //// Add Custom Layer 1d 3h
            /* datasetPPM = 'precip_3hr_1d'
                        field = 'precip'
                        startDate = formatDate(TODAY_DATE, '-')
                        endDate = formatDate(TODAY_DATE, '-')
                        const urlGeoJSONs = getUrlGeoJSONs(datasetPPM, startDate, endDate)
                        addCustomGeoJSONLayer(map.current, datasetPPM, urlGeoJSONs[0], field, EXCESS_RAIN_GRADIENT_COLORS, accRain1d)*/

            //// Add Custom Layer 3d
            datasetPPM = 'precip_3d'
            field = 'precip'
            let urlGeoJSON = getUrlGeoJSON(datasetPPM)
            addCustomGeoJSONLayer(map.current, datasetPPM, urlGeoJSON, field, EXCESS_RAIN_GRADIENT_COLORS)

            //// Add Custom Layer 7d
            datasetPPM = 'precip_7d'
            field = 'precip'
            urlGeoJSON = getUrlGeoJSON(datasetPPM)
            addCustomGeoJSONLayer(map.current, datasetPPM, urlGeoJSON, field, EXCESS_RAIN_GRADIENT_COLORS)


            //// Add Custom Layer accumulation 1d 3h
            //addCustomGeoJSONLayer(map.current, 'precip_3hr_1d', URL_ROOT + 'rain/gpm_realtime/gpm_1d.geojson', 'precip', EXCESS_RAIN_GRADIENT_COLORS, accRain1d)

            //// Add Custom Layer forecast 1d
            //addCustomGeoJSONLayer(map.current, 'fcstRain1d', URL_ROOT + 'rain/gfs_realtime/gfs_00d_01d.geojson', 'val', EXCESS_RAIN_GRADIENT_COLORS, fcstRain1d)

            //// Add Custom Layer forecast 5d
            //addCustomGeoJSONLayer(map.current, 'fcstRain5d', URL_ROOT + 'rain/gfs_realtime/gfs_00d_05d.geojson', 'val', EXCESS_RAIN_GRADIENT_COLORS, fcstRain5d)



            //// *** Pulsing dot
            map.current.addImage('pulsing-dot-current', pulsatingDot(200, 'red'), { pixelRatio: 2 }); // pulsing dot source
            map.current.addImage('pulsing-dot', selectedPulsatingDot, { pixelRatio: 2 }); // pulsing dot source

            //// *** Static dot source
            map.current.addSource('static-dot-source', {
                'type': 'geojson',
                'data': static_dot_geojson,
            });

            //// *** Static dot layer
            map.current.addLayer({
                'id': 'static-dot-layer',
                'type': 'circle',
                'source': 'static-dot-source' ,
                'visibility': 'none',
                'paint': {
                    'circle-radius': 8,
                    'circle-color': 'black',
                    'circle-stroke-color': 'white',
                    'circle-stroke-width': 2,
                }
            })

            map.current.addLayer({
                'id': 'pulsing-dot-layer',
                'type': 'symbol',
                'source': 'pulsing-dot-source',
                'layout': {
                    'icon-image': 'pulsing-dot'
                },
            });

            if (map.current.getSource('latest-source')) {
                map.current.addLayer({
                    'id': 'layer-with-pulsing-dot',
                    'type': 'symbol',
                    'source': 'latest-source',
                    'layout': {
                        'icon-image': 'pulsing-dot-current'
                    },
                    'filter': ['all', ['==', '$type', 'Point'], ['>', 'DTG', PREV_DTG], ['<', 'DTG', NEXT_DTG]],
                }); // pulsing dot layer
            }
        })

    }, [] );

    //// on stormPositions, draw pulsating dot at current (interpolated) position
    /* useEffect(() => {
            if (selectedDTG && stormPositions.length > 0) {
                let prev = stormPositions.filter(x => x.DTG < TODAY).reverse()[0]
                let next = stormPositions.filter(x => x.DTG > TODAY)[0]
                let today_date = new Date()
                let prev_date = new Date(prev.DTG)
                let next_date = new Date(next.DTG)
                let f = (today_date - prev_date) / (next_date - prev_date)
                let x = prev.coordinates[0] + f * (next.coordinates[0] - prev.coordinates[0])
                let y = prev.coordinates[1] + f * (next.coordinates[1] - prev.coordinates[1])
                let coordinates = [x, y]
                console.log('hi')
            }
        }, [stormPositions])*/

    //// on selectedDTG change, push the coordinates of the pulsing dot
    useEffect(() => {
        if (selectedDTG && selectedStormData.stormPositions.length > 0) {

            console.log('useEffect on selectedDTG')
            console.log('\t' + selectedStorm.storm_name)

            // get coordinates and speed at selection's time
            let selection = selectedStormData.stormPositions.filter(x => x.DTG === selectedDTG)[0]
            let coordinates = selection.coordinates
            let speed = selection.VMAX

            marker.geometry.coordinates = coordinates

            // remove previous pulsating dot
            // map.current.removeImage('pulsing-dot')
            // map.current.addImage('pulsing-dot', pulsatingDot(125, WIND_COLORS[windCategory(speed)]), { pixelRatio: 2 });

            // update coordinates of pulsating dot
            static_dot_geojson.features.push(marker)
            map.current.getSource('static-dot-source').setData(static_dot_geojson)
            map.current.setPaintProperty('static-dot-layer', 'circle-color', WIND_COLORS[windCategory(speed)])
            map.current.setLayoutProperty('static-dot-layer', 'visibility', 'visible')

            // move pulsing-dot-layer to top
            map.current.moveLayer('static-dot-layer')

            // zoom on pulsating dot
            map.current.flyTo({center: coordinates, speed: 0.8, essential: true})
        }

        return () => {
            console.log('\tCLEANUP')
            map.current.setLayoutProperty('static-dot-layer', 'visibility', 'none')
        }

    }, [selectedDTG])

    //// update data and selected position of the selected flood
    useEffect(() => {
        if (selectedFlood && mapLoaded) { // the layers of a flood can only be added once the style of the map is there
            // read the json file (selectedFlood) and load the data in selectedFloodData
            getFloodData(URL_ROOT_FLE + selectedFlood)


        }
    }, [selectedFlood, mapLoaded])

    //// stochastic (risk) data: only downloaded for the country of a selected flood, the first time one is selected
    useEffect(() => {
        const match = selectedFlood ? selectedFlood.match(/\/(\w{3})\//) : null
        const country = match && match[1]
        if (!country || !COUNTRIES_STOCHASTIC.includes(country) || stochasticRequested.current[country]) return
        stochasticRequested.current[country] = true
        setStochasticLoading(loading => ({...loading, [country]: true}))
        Promise.all([getCountryStochasticPopulationData(country), getCountryStochasticEconomicData(country)]).then(([population, economic]) => {
            if (population) setCountryStochasticPopulationData(previous => ({...previous, ...population}))
            if (economic) setCountryStochasticEconomicData(previous => ({...previous, ...economic}))
            if (!population || !economic) delete stochasticRequested.current[country] // tried again the next time a flood of this country is selected
            setStochasticLoading(loading => ({...loading, [country]: false}))
        })
    }, [selectedFlood])

    useEffect(() => {
            if (selectedFloodData) {
                //TODO: restore the following
                flyToFlood(map.current, selectedFloodData)

                console.log('selectedFloodData.max_depth_file', selectedFloodData.max_depth_file)

                // clear the current flood
                if (displayedFlood) {
                    removeSourceLayerFlood(map.current, displayedFlood)
                    updateDisplayedFlood(null)
                }

                // first check that the source exists, if not, add it
                if (!map.current.getSource(selectedFloodData.max_depth_file)) {
                    addSourceLayerFlood(map.current, selectedFloodData.max_depth_file)
                    updateDisplayedFlood(selectedFloodData.max_depth_file)
                }

                if (map.current.getSource('flood-source')) {
                    //updateSourceFlood(map.current, selectedFloodData)
                    //showHideFlood(map.current, selectedFloodData, layers, true)
                } else {
                    //addSourceFlood(map.current, selectedFloodData)
                    //drawFlood(map.current, selectedFloodData, currentFlood)
                }
            }
        }
        , [selectedFloodData])

    //// update data and selected position of the selected storm
    useEffect(() => {
        if (selectedStorm) {
            updateSelectedDTG(null) // reinitialize the selected DTG (in case of storm change for instance)
            fly(map.current, selectedStorm) // custom function to zoom to the bounding box of the selected storm

            // if the source exists, show the selected storm and layers, if not, add the source
            if (map.current.getSource('latest-source') || (map.current.getSource('latest-source-nc'))) { //TODO:
                updateSourceStorm(map.current, selectedStorm)
                showHideStorm(map.current, selectedStorm, layers, true)
            } else {
                addSourceStorm(map.current, selectedStorm)
                drawStorm(map.current, selectedStorm, currentStorm)
            }
            ///
            getStormData(selectedStorm.shp) // TODO: write a function that takes selectedStorm and spits out all the functions below
            ///
            getLatestStormID(selectedStorm.shp)
            getCurrentData(selectedStorm.shp)
            getStormPositions(selectedStorm.shp)
            getCurrentLosses(selectedStorm.losses)
            getVmax(selectedStorm.shp)
            getMaxSurge(selectedStorm.nc)
            map.current.on('click', 'storm-icons-layer', handleClick) //TODO: rename the 'storm-icon-layer'
        }

        // update the selected DTG once a storm's circle is clicked
        function handleClick(e) {
            if (e.features.length > 0) {
                console.log('\t' + 'clicked on circle and updates selectedDTG ' + selectedStorm.storm_name)
                updateSelectedDTG(e.features[0].properties.DTG)
            }
        }

        // clean-up
        return () => {
            map.current.off('click', 'storm-icons-layer', handleClick)
        }
    }, [selectedStorm])

    /// update the visibility of dynamic layers (independent of a selected storm)
    useEffect(() => {
        for (const [layerId, isVisible] of Object.entries(layers)) {
            if (map.current.getLayer(layerId)) {
                if (isVisible) loadLayerData(layerId);
                map.current.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
            }
        }
    }, [accRain3h, accRain1d, fcstRain1d, fcstRain5d, accRain1dVideo, pastPeakWind, pastPeakWater])

    /// update the visibility of dynamic layers (dependent of a selected storm)
    useEffect(() => {
        if (selectedStorm) {
            for (const [layerId, isVisible] of Object.entries(layers)) {
                if (map.current.getLayer(layerId)) {
                    if (isVisible) loadLayerData(layerId);
                    map.current.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
                }
            }
        }
    }, [pastPeakWind, pastPeakWater, pastRainTotal])

    /* // get the country data
        useEffect(() => {
            console.log('getting country data')
            console.log(getCountryData('moz'))
            Promise.all(COUNTRIES.map(getCountryData)).then((results) => {
                setCountryData(results.reduce((result, countryData, index) => {
                    const country = COUNTRIES[index];
                    result[country] = {
                        ongoing: countryData.ongoing,
                        ongoing_event_year: countryData.ongoing_event_year,
                        ongoing_event_month: countryData.ongoing_event_month,
                        ongoing_event_day: countryData.ongoing_event_day,
                        stats: countryData.stats,
                        total_events_country: countryData.total_events_country,
                        total_days_country: countryData.total_days_country,
                        peak_year: countryData.peak_year,
                        year_by_year: countryData.year_by_year,
                    };
                    return result;
                }, {}));
            });
        }, []);*/

    // get the flood data of the countries: a few at a time, smallest files first, so that the first countries can be
    // used early and the connection is not saturated by the biggest files (each country is stored as it arrives, see getCountryData)
    useEffect(() => {
        const queue = [...COUNTRIES_LOAD_ORDER.filter(country => COUNTRIES.includes(country)), ...COUNTRIES.filter(country => !COUNTRIES_LOAD_ORDER.includes(country))];
        const next = () => {
            const country = queue.shift();
            if (country) getCountryData(country).then(next);
        };
        for (let i = 0; i < COUNTRIES_LOAD_CONCURRENCY; i++) next();
    }, []);

// Parse ongoing events from countryData
    useEffect(() => {
        if (countryData) {
            var list = {};
            Object.keys(countryData).forEach(function(countryCode) {
                var countryInfo = countryData[countryCode];

                if (countryInfo && countryInfo.ongoing_event) {
                    var ongoing_event = countryInfo.ongoing_event;

                    // Check if it's an object and not null
                    if (ongoing_event && typeof ongoing_event === 'object') {

                        // Case 1: Current version
                        // It is a single event object containing a "start_date" field
                        if (ongoing_event.start_date) {
                            var dateKey = ongoing_event.start_date;
                            var singleEventDict = {};
                            singleEventDict[dateKey] = ongoing_event;

                            list[countryCode] = singleEventDict;
                        }

                            // Case 2: Future version
                        // It is already a dictionary of events { "ID": { data } }
                        else if (Object.keys(ongoing_event).length > 0) {
                            // We assume if it's an object without start_date,
                            // it's the dictionary of multiple events
                            list[countryCode] = ongoing_event;
                        }
                    }
                }
            });
            setOngoingEvents(list);
        }
    }, [countryData]);

// Automatically select the most recent ongoing event if available and no flood is currently selected
    useEffect(() => {
        var countryKeys = COUNTRIES.filter(function(countryCode) { return ongoingEvents[countryCode]; });
        if (countryKeys.length > 0 && !selectedFlood) {
            var firstCountryCode = countryKeys[0];
            // the countries arrive one by one: the ones listed before it come first if they also have an ongoing event, so they are awaited
            var earlierCountries = COUNTRIES.slice(0, COUNTRIES.indexOf(firstCountryCode));
            if (earlierCountries.some(function(countryCode) { return loadingCountries.includes(countryCode); })) return;
            var eventsList = ongoingEvents[firstCountryCode];

            if (eventsList && typeof eventsList === 'object') {
                // Sort keys descending to get the most recent date first
                var sortedEventIds = Object.keys(eventsList).sort().reverse();

                if (sortedEventIds.length > 0) {
                    var latestEventId = sortedEventIds[0];
                    var latestEventData = eventsList[latestEventId];
                    var year = latestEventData.ongoing_event_year;

                    // Traditional "safe" access instead of ?.
                    var countryYearData = (countryData[firstCountryCode] &&
                        countryData[firstCountryCode]['year_by_year'])
                        ? countryData[firstCountryCode]['year_by_year'][year]
                        : null;

                    if (countryYearData && countryYearData[latestEventId]) {
                        var eventPath = countryYearData[latestEventId].path;

                        updateSelectedCountry(firstCountryCode);
                        updateSelectedFlood(eventPath);
                    }
                }
            }
        }
    }, [ongoingEvents, selectedFlood, updateSelectedCountry, updateSelectedFlood, countryData, loadProgress.pending.length]);


    //// Loader
    // the first screen only needs the translations and the style of the map
    const splashOpen = !ready || !mapLoaded;

    useEffect(() => {
        if (!splashOpen) markOnce('app:splash-cleared')
    }, [splashOpen])

    useEffect(() => {
        if (!splashOpen && loadProgress.pending.length === 0) markOnce('app:loaded')
    }, [splashOpen, loadProgress.pending.length])

    return (
        <div ref={mapContainer} className="map-container" id={'map-container'}>

            {/* Loader, in two stages (see loadingBackdrop.js): the full-screen one blocks any click until the first screen can be displayed, */}
            {/* then a thin bar follows the loading of the countries while the controls are enabled as their data arrives */}
            {/* t() returns the raw key until the translations are loaded */}
            <LoadingBackdrop
                open={splashOpen}
                slowMessage={ready ? t('loader.slow') : ''}
                retryLabel={ready ? t('loader.retry') : 'Retry'}
                done={loadProgress.done}
                total={LOAD_STEPS.length}
                message={ready ? t('loader.message') : ''}
                step={loadStepText}
            />
            <LoadingBar
                open={!splashOpen && (loadProgress.pending.length > 0 || loadProgress.errors.length > 0)}
                error={loadProgress.errors.length === 0 ? '' :
                    t('loader.error', {steps: loadProgress.errors.map(loadStepLabel).join(', ')})}
                retryLabel={t('loader.retry')}
                continueLabel={t('loader.dismiss')}
                onContinue={() => setLoadProgress(p => ({...p, errors: []}))}
                done={loadProgress.done}
                total={LOAD_STEPS.length}
                label={loadStepText}
            />

            <div className={'logo-title'}>
                <FleTitle />
            </div>

            <div className={'alert'}>
                <OfflineNotice />
                <Collapse in={floodLoadError}>
                    <Alert
                        severity="error"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setFloodLoadError(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                    >
                        {t('alert.flood_load_failed')}
                    </Alert>
                </Collapse>
            </div>

            <div className={'copyright'}>
                <p>&copy; African Risk Capacity</p>
            </div>

            <div className={'arc-logo'}>
                <ArcLogo />
            </div>

            {/* top-menus / bottom-dock : plain wrappers on desktop, they lay out their content on phones (see App.css) */}
            <div className={'top-menus'}>
            <div className={'storm-menu'}>
                <SelectedFlood countryData={countryData} loadingCountries={loadingCountries} failedCountries={failedCountries} />
            </div>

            <div className={'exploration-menu'}>
                <SelectedExploration />
            </div>
            </div>

            <div>
                <Settings />
            </div>

            {/* Ongoing Events Overlay */}
            <OngoingEventsOverlay
                ongoingEvents={ongoingEvents}
                selectedFlood={selectedFlood}
                updateSelectedFlood={updateSelectedFlood}
                updateSelectedCountry={updateSelectedCountry}
                countryData={countryData}
            />

            {/* <div>
                    <Languages />
                </div>*/}


            {/* {!currentStorm
                &&
                <div className={'alert'}>
                    <Collapse in={openAlert}>
                        <Alert
                            severity="warning"
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setOpenAlert(false);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                        >
                            {t('alert.no_active_storm')}
                        </Alert>
                    </Collapse>
                </div>
                }*/}

            <div className={'info'}>
                {(stormName) &&
                    <div className={'storm-info'}>
                        <Divider />
                        {/*{storm && <div><span>Year: <strong className="storm-year">{stormYear}</strong></span></div>}*/}
                        {storm && <div><span>ID: <strong className="storm-id">{stormId}</strong></span></div>}
                    </div>
                }
                {
                    storm &&
                    <div className={'layer-info'}>
                        <div>Layers</div>
                        <Divider />
                        {
                            Object.entries(layers).map(([key, value]) =>
                                (
                                    <div>
                                        {value && <CheckCircleIcon className={'check-circle-icon'}/>}
                                        {value && key}
                                    </div>
                                )
                            )
                        }
                    </div>
                }
            </div>

            {selectedFloodData &&
                <div>
                    <ReportFloodPopUp
                        selectedFloodData={selectedFloodData}
                        map={map.current}
                    />
                </div>
            }

            <div className={'bottom-dock'}>
            <div>
                {selectedStorm && currentLosses && selectedStormData &&
                    <Suspense fallback={null}>
                    <StormOverlay
                        selectedStorm = {selectedStorm}
                        selectedStormData = {selectedStormData}
                        currentLosses = {currentLosses}
                        stormPositions = {selectedStormData.stormPositions}
                        todayData = {todayData}
                        vmax = {selectedStormData.vmax}
                        maxSurge = {maxSurge}
                        currentStorm = {currentStorm}
                        open = {true}
                        clearMapSelectedStorm={clearMapSelectedStorm}
                    />
                    </Suspense>
                }
            </div>

            <div>
                {selectedFloodData &&
                    <Suspense fallback={null}>
                    <FloodOverlay
                        data={selectedFloodData}
                        selectedFlood={selectedFlood}
                        countryStochasticPopulationData = {countryStochasticPopulationData}
                        countryStochasticEconomicData = {countryStochasticEconomicData}
                        stochasticLoading={stochasticLoading}
                        width={WIDTH_FLOOD_OVERLAY}
                        height={HEIGHT_FLOOD_OVERLAY}
                    />
                    </Suspense>
                }
            </div>

            {
                selectedFloodData &&
                <div className={'legend'}>
                    <Box sx={{ minWidth: 150, boxShadow: 0, marginLeft:0}}>
                        <Legend
                            legendTitle={`${t('flood_overlay.flood_depth.title')} (m)`}
                            colorStops={FLOOD_DEPTH_GRADIENT_COLORS}
                            id={'flood_depth_legend'}
                            customFunction={findDepthByBand}
                        />
                    </Box>
                </div>
            }
            </div>

        </div>
    );
}
